import React, { useEffect, useState } from "react";
import { Add, LocationOn } from "@mui/icons-material";
import { Box, Button, Paper, Typography } from "@mui/material";
import { toast } from "react-toastify";

import FormDrawer from "components/Shared/FormDrawer";
import CollectionStopsForm from "./CollectionStopsForm";
import CollectionStopList from "./CollectionStopList";

import { useShipmentFulfillmentDetailContext } from "contexts/ShipmentFulfillmentContext.js";
import {
  useAddCollectionStopMutation,
  useLazyListCollectionStopsQuery,
} from "store/services/collectionApi";

const CollectionStopsTab = () => {
  const [showStopForm, setShowStopForm] = useState(false);
  const [selectedStop, setSelectedStop] = useState(null);

  const { fulfillmentDetail, refetch: refetchFulfillment } = useShipmentFulfillmentDetailContext();
  const collectionId = fulfillmentDetail?._id;

  // Calculate shipment status
  const isLoaded = fulfillmentDetail?.shipmentId?.some(
    (shipment) => shipment.status === "Loaded"
  );
const validStatuses = [
  "Unloaded",
  "Deconsolidation",
  "CrossDocked",
  "SendOut",
  "Warehousing",
  "Delivered",
  "DeliveryFailed",
  "Return",
  "Cancelled",
];

const isDelivered = fulfillmentDetail?.shipmentId?.some(
  (shipment) => validStatuses.includes(shipment.status)
);


  // RTK Query
  const [fetchStops, { data: stopsRes, isError }] = useLazyListCollectionStopsQuery();
  const [addShipmentStop, { isLoading: addingStop }] = useAddCollectionStopMutation();

  // Polling to keep fulfillmentDetail updated
  useEffect(() => {
    if (!collectionId) return;

    // Start polling to refetch fulfillmentDetail every 10 seconds
    const pollingInterval = setInterval(() => {
      refetchFulfillment();
    }, 10000); // Poll every 10 seconds

    // Clean up polling on unmount
    return () => clearInterval(pollingInterval);
  }, [collectionId, refetchFulfillment]);

  // Fetch stops when collectionId and isLoaded are available
  useEffect(() => {
    if (collectionId && isLoaded) {
      fetchStops({ collectionId }).catch((err) => {
        toast.error("Failed to fetch stops!");
      });
    }
  }, [collectionId, fetchStops, isLoaded]);

  // Handle errors
  useEffect(() => {
    if (isError) {
      toast.error("Failed to fetch stops!");
    }
  }, [isError]);

  const handleCancelStop = () => {
    setShowStopForm(false);
    setSelectedStop(null);
  };

  const handleAddShipmentStop = async (data) => {
    if (!data?.stopLocation?.location) {
      toast.error("Stop location is required!");
      return;
    }

    try {
      const payload = {
        collectionId: collectionId,
        stopLocation: {
          location: data.stopLocation.location || " ",
          lat: data.stopLocation.lat ?? null,
          lon: data.stopLocation.lon ?? null,
        },
        stopTime: data.stopTime,
        status: data.status,
      };

      await addShipmentStop(payload).unwrap();
      toast.success("Stop successfully added!");
      handleCancelStop();
      fetchStops({ collectionId });
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't add stop!");
    }
  };

  return (
    <>
      {isLoaded || isDelivered ? (
        <>
          <Box display="flex" justifyContent="end" padding={3}>
            {!showStopForm && (
              <Button
                startIcon={<Add />}
                variant="outlined"
                onClick={() => setShowStopForm(true)}
                disabled={isDelivered}
              >
                Add Stop
              </Button>
            )}
          </Box>
          <CollectionStopList stops={Array.isArray(stopsRes?.stops) ? stopsRes.stops : []} />
          <FormDrawer
            title={selectedStop ? "Edit Shipment Stop" : "Add Shipment Stop"}
            open={showStopForm}
            onClose={handleCancelStop}
          >
            <CollectionStopsForm
              isBusy={addingStop}
              stop={selectedStop}
              onCancel={handleCancelStop}
              onSubmit={handleAddShipmentStop}
            />
          </FormDrawer>
        </>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "background.paper",
            borderRadius: 2,
            p: 4,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: "center",
              maxWidth: 500,
              borderRadius: 2,
              width: "100%",
            }}
          >
            <Box
              sx={{
                bgcolor: "action.hover",
                width: 80,
                height: 80,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              <LocationOn color="disabled" sx={{ fontSize: 40 }} />
            </Box>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              Stops Not Available
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Stops can only be tracked when the shipment is loaded or delivered.
            </Typography>
          </Paper>
        </Box>
      )}
    </>
  );
};

export default CollectionStopsTab;